import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  Container,
  InputAdornment,
  Alert,
  LinearProgress,
  Snackbar
} from '@mui/material';
import {
  AccountBalance as WalletIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  CreditCard as CardIcon,
  Payment as PayPalIcon,
  AccountBalanceWallet as BankIcon,
  Download as DownloadIcon,
  AttachMoney as MoneyIcon,
  CheckCircle as CheckCircleIcon,
  Pending as PendingIcon,
  Error as ErrorIcon
} from '@mui/icons-material';
import { walletService, realtimeService, handleFirebaseError } from '../../services/firebaseService';

export default function Wallet({ user }) {
  const [walletData, setWalletData] = useState({
    balance: 0,
    pendingEarnings: 0,
    totalEarnings: 0,
    totalSpent: 0
  });
  const [transactions, setTransactions] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState('');
  const [amount, setAmount] = useState('');
  const [tabValue, setTabValue] = useState(0);
  const [paymentMethod, setPaymentMethod] = useState('');
  const [loading, setLoading] = useState(false);
  const [filterPeriod, setFilterPeriod] = useState('all');
  const [dataLoading, setDataLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (!user?.uid) return;

    // Load initial wallet data
    loadWalletData();
    loadTransactions();

    // Set up real-time wallet listener
    const unsubscribeWallet = realtimeService.subscribeToWallet(user.uid, (walletData) => {
      setWalletData(walletData);
    });

    return () => {
      unsubscribeWallet();
    };
  }, [user]);

  const loadWalletData = async () => {
    try {
      setDataLoading(true);
      const wallet = await walletService.getWallet(user.uid);
      if (wallet) {
        setWalletData(wallet);
      } else {
        // Initialize wallet if it doesn't exist
        await walletService.initializeWallet(user.uid);
        setWalletData({
          balance: 0,
          pendingEarnings: 0,
          totalEarnings: 0,
          totalSpent: 0
        });
      }
    } catch (error) {
      console.error('Error loading wallet:', error);
      setError(handleFirebaseError(error));
    } finally {
      setDataLoading(false);
    }
  };

  const loadTransactions = async () => {
    try {
      const userTransactions = await walletService.getUserTransactions(user.uid, { limit: 50 });
      setTransactions(userTransactions);
    } catch (error) {
      console.error('Error loading transactions:', error);
      setError(handleFirebaseError(error));
    }
  };

  const handleWithdraw = async () => {
    if (!amount || parseFloat(amount) > walletData.balance) return;
    
    try {
      setLoading(true);
      const withdrawAmount = parseFloat(amount);
      
      // Create withdrawal transaction
      const transactionData = {
        type: 'withdrawal',
        amount: -withdrawAmount,
        description: `Withdrawal to ${paymentMethod === 'bank' ? 'Bank Account' : 'PayPal'}`,
        status: 'pending',
        method: paymentMethod,
        metadata: {
          originalAmount: withdrawAmount,
          fee: 0, // You can add withdrawal fees here
          currency: 'USD'
        }
      };

      const transactionId = await walletService.addTransaction(user.uid, transactionData);
      
      // Update wallet balance
      await walletService.updateBalance(user.uid, withdrawAmount, 'subtract');
      
      // Reload transactions to show the new one
      await loadTransactions();
      
      setSuccess('Withdrawal request submitted successfully!');
      setDialogOpen(false);
      setAmount('');
      setPaymentMethod('');
    } catch (error) {
      console.error('Error processing withdrawal:', error);
      setError(handleFirebaseError(error));
    } finally {
      setLoading(false);
    }
  };

  const handleAddFunds = async () => {
    if (!amount) return;
    
    try {
      setLoading(true);
      const depositAmount = parseFloat(amount);
      
      // Create deposit transaction
      const transactionData = {
        type: 'deposit',
        amount: depositAmount,
        description: `Deposit from ${paymentMethod === 'card' ? 'Credit Card' : 'PayPal'}`,
        status: 'completed', // In real app, this would be pending until payment is confirmed
        method: paymentMethod,
        metadata: {
          originalAmount: depositAmount,
          fee: 0, // You can add processing fees here
          currency: 'USD'
        }
      };

      const transactionId = await walletService.addTransaction(user.uid, transactionData);
      
      // Update wallet balance
      await walletService.updateBalance(user.uid, depositAmount, 'add');
      
      // Reload transactions to show the new one
      await loadTransactions();
      
      setSuccess('Funds added successfully!');
      setDialogOpen(false);
      setAmount('');
      setPaymentMethod('');
    } catch (error) {
      console.error('Error adding funds:', error);
      setError(handleFirebaseError(error));
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleOpenDialog = (type) => {
    setDialogType(type);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setAmount('');
    setPaymentMethod('');
    setError('');
  };

  const handleCloseSnackbar = () => {
    setError('');
    setSuccess('');
  };

  const getTransactionIcon = (type, status) => {
    if (status === 'pending') return <PendingIcon color="warning" />;
    if (status === 'failed') return <ErrorIcon color="error" />;
    
    switch (type) {
      case 'earning':
        return <TrendingUpIcon color="success" />;
      case 'spending':
        return <TrendingDownIcon color="error" />;
      case 'withdrawal':
        return <RemoveIcon color="warning" />;
      case 'deposit':
        return <AddIcon color="primary" />;
      default:
        return <MoneyIcon />;
    }
  };

  const getStatusChip = (status) => {
    const colors = {
      completed: 'success',
      pending: 'warning',
      failed: 'error'
    };
    return <Chip label={status} color={colors[status] || 'default'} size="small" />;
  };

  // Filter transactions by tab and period (no sample data)
  const filterByPeriod = (transaction) => {
    if (filterPeriod === 'all') return true;
    const now = new Date();
    const createdAt = transaction.createdAt ?
      (transaction.createdAt.toDate ? transaction.createdAt.toDate() : new Date(transaction.createdAt)) : null;
    if (!createdAt) return false;
    if (filterPeriod === 'week') {
      const weekAgo = new Date(now);
      weekAgo.setDate(now.getDate() - 7);
      return createdAt >= weekAgo;
    }
    if (filterPeriod === 'month') {
      const monthAgo = new Date(now);
      monthAgo.setMonth(now.getMonth() - 1);
      return createdAt >= monthAgo;
    }
    if (filterPeriod === 'year') {
      const yearAgo = new Date(now);
      yearAgo.setFullYear(now.getFullYear() - 1);
      return createdAt >= yearAgo;
    }
    return true;
  };

  const filteredTransactions = transactions
    .filter(transaction => {
      switch (tabValue) {
        case 0: return true;
        case 1: return transaction.type === 'earning';
        case 2: return transaction.type === 'spending';
        case 3: return ['withdrawal', 'deposit'].includes(transaction.type);
        default: return true;
      }
    })
    .filter(filterByPeriod);

  const StatCard = ({ title, value, icon, color = 'primary', prefix = '$' }) => (
    <Card sx={{ 
      height: '100%', 
      borderRadius: 3, 
      boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      border: '1px solid',
      borderColor: 'divider',
      '&:hover': { 
        transform: 'translateY(-4px)',
        boxShadow: '0 8px 25px rgba(0,0,0,0.15)'
      }
    }}>
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ flex: 1 }}>
            <Typography 
              color="text.secondary" 
              variant="subtitle2"
              sx={{ 
                fontSize: '0.875rem',
                fontWeight: 500,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                mb: 1
              }}
            >
              {title}
            </Typography>
            <Typography 
              variant="h4" 
              sx={{ 
                color: `${color}.main`, 
                fontWeight: 700,
                fontSize: { xs: '1.5rem', sm: '1.75rem' },
                lineHeight: 1.2
              }}
            >
              {prefix}{typeof value === 'number' ? value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00'}
            </Typography>
          </Box>
          <Box sx={{ 
            color: `${color}.main`, 
            opacity: 0.8,
            p: 1,
            borderRadius: 2,
            bgcolor: `${color}.main`,
            color: 'white',
            '& .MuiSvgIcon-root': { fontSize: '1.5rem' }
          }}>
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <Box sx={{ width: '100vw', minHeight: '100vh', p: 0, m: 0, background: 'transparent', boxSizing: 'border-box', overflowX: 'hidden' }}>
      {dataLoading ? (
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          minHeight: '60vh',
          flexDirection: 'column',
          gap: 2
        }}>
          <Typography variant="h6" color="text.secondary">Loading wallet data...</Typography>
          <LinearProgress sx={{ width: '300px' }} />
        </Box>
      ) : (
        <>
      {/* Header */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: 2,
        flexDirection: { xs: 'column', sm: 'row' },
        gap: { xs: 1, sm: 0 },
        pl: 0,
        pr: 0,
        ml: 0,
        mr: 0
      }}>
            <Typography 
              variant="h4" 
              sx={{ 
                fontWeight: 'bold', 
                fontSize: { xs: '1.75rem', sm: '2.125rem' },
                color: 'text.primary'
              }}
            >
              Wallet
            </Typography>
            <Box sx={{ 
              display: { xs: 'none', sm: 'flex' }, 
              gap: 2 
            }}>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => handleOpenDialog('deposit')}
                sx={{ 
                  borderRadius: 2,
                  px: 3,
                  py: 1,
                  textTransform: 'none',
                  fontWeight: 600
                }}
              >
                Add Funds
              </Button>
              <Button
                variant="contained"
                startIcon={<RemoveIcon />}
                onClick={() => handleOpenDialog('withdraw')}
                sx={{ 
                  bgcolor: '#f44336', 
                  '&:hover': { bgcolor: '#d32f2f' },
                  borderRadius: 2,
                  px: 3,
                  py: 1,
                  textTransform: 'none',
                  fontWeight: 600,
                  boxShadow: 2
                }}
              >
                Withdraw
              </Button>
            </Box>
          </Box>

      {/* Balance Cards */}
      <Grid container spacing={2} sx={{ mb: 2, pl: 0, pr: 0, ml: 0, mr: 0 }}>
        <Grid item xs={12} sm={6} lg={3}>
          <StatCard 
            title="Available Balance" 
            value={walletData.balance} 
            icon={<WalletIcon />} 
            color="primary"
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <StatCard 
            title="Pending Earnings" 
            value={walletData.pendingEarnings} 
            icon={<PendingIcon />} 
            color="warning"
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <StatCard 
            title="Total Earnings" 
            value={walletData.totalEarnings} 
            icon={<TrendingUpIcon />} 
            color="success"
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <StatCard 
            title="Total Spent" 
            value={walletData.totalSpent} 
            icon={<TrendingDownIcon />} 
            color="error"
          />
        </Grid>
      </Grid>

      {/* Quick Actions (Mobile) */}
      <Paper sx={{ 
        p: 2, 
        mb: 2, 
        display: { xs: 'block', sm: 'none' }, 
        borderRadius: 2,
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
        border: '1px solid',
        borderColor: 'divider',
        ml: 0,
        mr: 0
      }}>
        <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, color: 'text.primary' }}>
          Quick Actions
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog('deposit')}
              sx={{
                py: 1.5,
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                border: '2px solid',
                '&:hover': {
                  border: '2px solid'
                }
              }}
            >
              Add Funds
            </Button>
          </Grid>
          <Grid item xs={6}>
            <Button
              fullWidth
              variant="contained"
              startIcon={<RemoveIcon />}
              onClick={() => handleOpenDialog('withdraw')}
              sx={{ 
                bgcolor: '#f44336', 
                '&:hover': { bgcolor: '#d32f2f' },
                py: 1.5,
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                boxShadow: 2
              }}
            >
              Withdraw
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Transaction History */}
      <Paper sx={{ 
        borderRadius: 2,
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
        border: '1px solid',
        borderColor: 'divider',
        overflow: 'hidden',
        ml: 0,
        mr: 0
      }}>
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', bgcolor: 'grey.50' }}>
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            mb: 3,
            flexDirection: { xs: 'column', sm: 'row' },
            gap: { xs: 2, sm: 0 }
          }}>
            <Typography variant="h5" sx={{ fontWeight: 600, color: 'text.primary' }}>
              Transaction History
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel>Filter Period</InputLabel>
                <Select
                  value={filterPeriod}
                  onChange={(e) => setFilterPeriod(e.target.value)}
                  label="Filter Period"
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="all">All Time</MenuItem>
                  <MenuItem value="week">This Week</MenuItem>
                  <MenuItem value="month">This Month</MenuItem>
                  <MenuItem value="year">This Year</MenuItem>
                </Select>
              </FormControl>
              <IconButton 
                sx={{ 
                  bgcolor: 'primary.main', 
                  color: 'white',
                  '&:hover': { bgcolor: 'primary.dark' }
                }}
              >
                <DownloadIcon />
              </IconButton>
            </Box>
          </Box>
          
          <Tabs 
            value={tabValue} 
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 600,
                minHeight: 48,
                px: 3
              },
              '& .MuiTabs-indicator': {
                height: 3,
                borderRadius: '3px 3px 0 0'
              }
            }}
          >
            <Tab label="All Transactions" />
            <Tab label="Earnings" />
            <Tab label="Spending" />
            <Tab label="Transfers" />
          </Tabs>
        </Box>

        <TableContainer sx={{ minHeight: 300 }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, bgcolor: 'grey.50' }}>Type</TableCell>
                <TableCell sx={{ fontWeight: 600, bgcolor: 'grey.50' }}>Description</TableCell>
                <TableCell sx={{ fontWeight: 600, bgcolor: 'grey.50' }}>Date</TableCell>
                <TableCell sx={{ fontWeight: 600, bgcolor: 'grey.50' }}>Status</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600, bgcolor: 'grey.50' }}>Amount</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredTransactions.length > 0 ? (
                filteredTransactions.map((transaction) => (
                  <TableRow 
                    key={transaction.id} 
                    hover
                    sx={{ 
                      '&:hover': { bgcolor: 'grey.50' },
                      '&:last-child td': { border: 0 }
                    }}
                  >
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box sx={{
                          p: 1,
                          borderRadius: 2,
                          bgcolor: transaction.type === 'earning' ? 'success.light' :
                                   transaction.type === 'spending' ? 'error.light' :
                                   transaction.type === 'deposit' ? 'primary.light' : 'warning.light',
                          color: 'white'
                        }}>
                          {getTransactionIcon(transaction.type, transaction.status)}
                        </Box>
                        <Typography variant="body2" sx={{ textTransform: 'capitalize', fontWeight: 500 }}>
                          {transaction.type}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ color: 'text.primary' }}>
                        {transaction.description}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {transaction.createdAt ? 
                          new Date(transaction.createdAt.toDate ? transaction.createdAt.toDate() : transaction.createdAt)
                            .toLocaleDateString('en-US', { 
                              year: 'numeric', 
                              month: 'short', 
                              day: 'numeric' 
                            }) : 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {getStatusChip(transaction.status)}
                    </TableCell>
                    <TableCell align="right">
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          color: transaction.amount >= 0 ? 'success.main' : 'error.main',
                          fontWeight: 600,
                          fontSize: '0.95rem'
                        }}
                      >
                        {transaction.amount >= 0 ? '+' : ''}${Math.abs(transaction.amount).toFixed(2)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                      <MoneyIcon sx={{ fontSize: 64, color: 'text.disabled' }} />
                      <Typography variant="h6" color="text.secondary">
                        No transactions found
                      </Typography>
                      <Typography variant="body2" color="text.disabled">
                        {tabValue === 0 ? 'Your transactions will appear here once you start using your wallet' :
                         tabValue === 1 ? 'No earnings recorded yet' :
                         tabValue === 2 ? 'No spending recorded yet' :
                         'No transfers recorded yet'}
                      </Typography>
                      {tabValue === 0 && (
                        <Button
                          variant="contained"
                          startIcon={<AddIcon />}
                          onClick={() => handleOpenDialog('deposit')}
                          sx={{ mt: 2, borderRadius: 2, textTransform: 'none' }}
                        >
                          Add Your First Transaction
                        </Button>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Transaction Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 8px 24px rgba(0,0,0,0.10)'
          }
        }}
      >
        <DialogTitle sx={{ 
          pb: 1, 
          fontSize: '1.5rem', 
          fontWeight: 600,
          color: 'text.primary'
        }}>
          {dialogType === 'withdraw' ? 'Withdraw Funds' : 'Add Funds'}
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {loading && <LinearProgress sx={{ mb: 3, borderRadius: 1 }} />}
          
          <Grid container spacing={3} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Amount"
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2
                  }
                }}
                error={dialogType === 'withdraw' && parseFloat(amount) > walletData.balance}
                helperText={
                  dialogType === 'withdraw' && parseFloat(amount) > walletData.balance
                    ? 'Insufficient balance'
                    : dialogType === 'withdraw'
                    ? `Available: $${walletData.balance.toFixed(2)}`
                    : 'Enter the amount you want to add'
                }
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Payment Method</InputLabel>
                <Select
                  value={paymentMethod}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                  label="Payment Method"
                  sx={{
                    borderRadius: 2
                  }}
                >
                  {dialogType === 'withdraw' ? (
                    <>
                      <MenuItem value="bank">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <BankIcon />
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>Bank Account</Typography>
                            <Typography variant="caption" color="text.secondary">2-3 business days</Typography>
                          </Box>
                        </Box>
                      </MenuItem>
                      <MenuItem value="paypal">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <PayPalIcon />
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>PayPal</Typography>
                            <Typography variant="caption" color="text.secondary">Instant transfer</Typography>
                          </Box>
                        </Box>
                      </MenuItem>
                    </>
                  ) : (
                    <>
                      <MenuItem value="card">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <CardIcon />
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>Credit/Debit Card</Typography>
                            <Typography variant="caption" color="text.secondary">Instant deposit</Typography>
                          </Box>
                        </Box>
                      </MenuItem>
                      <MenuItem value="paypal">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <PayPalIcon />
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>PayPal</Typography>
                            <Typography variant="caption" color="text.secondary">Instant deposit</Typography>
                          </Box>
                        </Box>
                      </MenuItem>
                    </>
                  )}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
          
          {dialogType === 'withdraw' && (
            <Alert 
              severity="info" 
              sx={{ 
                mt: 3,
                borderRadius: 2,
                bgcolor: 'primary.light',
                color: 'primary.dark',
                '& .MuiAlert-icon': {
                  color: 'primary.main'
                }
              }}
            >
              <Typography variant="body2">
                Withdrawals are processed within 1-3 business days. You'll receive an email confirmation once the transfer is complete.
              </Typography>
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 2 }}>
          <Button 
            onClick={handleCloseDialog}
            sx={{ 
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: 2,
              px: 3
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={dialogType === 'withdraw' ? handleWithdraw : handleAddFunds}
            variant="contained"
            disabled={
              !amount || 
              !paymentMethod || 
              loading || 
              (dialogType === 'withdraw' && parseFloat(amount) > walletData.balance)
            }
            sx={{
              bgcolor: dialogType === 'withdraw' ? '#f44336' : 'primary.main',
              '&:hover': { 
                bgcolor: dialogType === 'withdraw' ? '#d32f2f' : 'primary.dark'
              },
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: 2,
              px: 4,
              boxShadow: 2
            }}
          >
            {loading ? 'Processing...' : (dialogType === 'withdraw' ? 'Withdraw Funds' : 'Add Funds')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity="error" 
          sx={{ 
            width: '100%',
            borderRadius: 2,
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)'
          }}
        >
          {error}
        </Alert>
      </Snackbar>
      
      <Snackbar
        open={!!success}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity="success" 
          sx={{ 
            width: '100%',
            borderRadius: 2,
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)'
          }}
        >
          {success}
        </Alert>
      </Snackbar>
        </>
      )}
    </Box>
  );
}
